import React from "react";
import {useNavigate} from "react-router-dom";
import {Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Typography, useTheme} from "@mui/material";
import {DescriptionRounded, ExpandMore} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {useFormContext, useWatch} from "react-hook-form";
import { IRaDetailView } from "../../models/models";
import { FormTextField } from "../../common/form-fields-wrappers";

const FinancialInfo = () => {
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const navigate = useNavigate();

    const raDetailForm = useFormContext<IRaDetailView>();

    const [businessAgreement, rate] = useWatch({control: raDetailForm.control, name: ["financialInfo.businessAgreement", "financialInfo.rate"]});

    const handleRateAction = () => {
        navigate(`/pricing/rate/base-rates?name=${rate}`);
    }

    const handleBusinessAgreementAction = () => {
        navigate(`/salesmarketing/businessagreementlist?businessAgreementCode=${businessAgreement}`);
    }

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant={"bodyLarge"}>
                    {t("raDetail.financialInfo.accordion.title")}
                </Typography>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container columnSpacing={2}>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.segment'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.segment")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.category'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.category")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.product'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.product")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-end"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.rate'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.rate")}
                            fullWidth
                            disabled
                        />
                        <IconButton title={t('raDetail.financialInfo.form.icon.rateDetail')}
                                    color="accent"
                                    onClick={() => handleRateAction()}
                                    sx={{mx: theme.spacing(0.5)}}
                                    disabled={!rate}
                        >
                            <DescriptionRounded />
                        </IconButton>
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-end"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.businessAgreement'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.businessAgreement")}
                            fullWidth
                            disabled
                        />

                        <IconButton title={t('raDetail.generalInfo.form.icon.businessAgreement')}
                                    color="accent"
                                    onClick={() => handleBusinessAgreementAction()}
                                    sx={{mx: theme.spacing(0.5)}}
                                    disabled={!businessAgreement}
                        >
                            <DescriptionRounded />
                        </IconButton>
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.creditType'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.creditType")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.firstPreauthCardNumber'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.firstPreauthCardNumber")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.secondPreauthCardNumber'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.secondPreauthCardNumber")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormTextField<IRaDetailView>
                            name='financialInfo.paymentCardNumber'
                            control={raDetailForm.control}
                            label={t("raDetail.financialInfo.form.label.paymentCardNumber")}
                            fullWidth
                            disabled
                        />
                    </Grid>

                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default FinancialInfo;